import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useFindMany } from "@gadgetinc/react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../../../api";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { OverflowFrame } from "../../../externalComponents";
import { CustomerContext } from "../../../providers";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Text } from "../../../externalComponents/tailwind/text";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../../externalComponents/tailwind/dropdown";
import {
  EllipsisHorizontalIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/20/solid";
import { Button } from "../../../externalComponents/tailwind/button";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Input } from "../../../externalComponents/tailwind/input";
import { formatDateTime } from "../../../functions/date-utils";
import { TagDeleteDialog } from "../../../externalComponents/TagDeleteDialog";
import { TagMergeDialog } from "../../../externalComponents/TagMergeDialog";

export default function CompanyTagsPage() {
  const { customer } = useContext(CustomerContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [renamingTagId, setRenamingTagId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [tagToMerge, setTagToMerge] = useState(null);
  const [mergingTagIds, setMergingTagIds] = useState([]);
  const renameInputRef = useRef(null);

  // Pagination state
  const NUM_ON_PAGE = 10;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  // URL parameter handling states
  const [targetPageFromUrl, setTargetPageFromUrl] = useState(null);
  const [isNavigatingFromUrl, setIsNavigatingFromUrl] = useState(false);
  const [isLoadingToTargetPage, setIsLoadingToTargetPage] = useState(false);
  const [hasFetchedAllData, setHasFetchedAllData] = useState(false);

  const _defaultPartialFields = {
    id: true,
    name: true,
    createdAt: true,
    lastContactTagCreatedAt: true,
    customerId: true,
  };
  const [fields, setFields] = useState(_defaultPartialFields);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [{ customerId: { equals: customer?.id } }],
  });

  // Search functionality
  const [{ data: searchData }] = useFindMany(api.tag, {
    search,
    select: { id: true },
  });

  useEffect(() => {
    if (search) {
      if (searchData) {
        const ids = searchData.map(({ id }) => id);
        setCriteria({
          filter: [
            { customerId: { equals: customer?.id } },
            { id: { in: ids.length > 0 ? ids : [0] } },
          ],
        });
        setFields(_defaultPartialFields);
        setHasFetchedAllData(false);
      }
    } else {
      setCriteria({
        ...cursor,
        filter: [{ customerId: { equals: customer?.id } }],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    }
  }, [searchData, search, customer?.id, cursor]);

  // Initialize page number from URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;

    if (initialPage > 1) {
      setTargetPageFromUrl(initialPage);
      setIsNavigatingFromUrl(true);
      setIsLoadingToTargetPage(true);
    } else {
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [location.search]);

  // Fetch tags with pagination
  const [{ data, fetching, error }] = useFindMany(api.tag, {
    ...criteria,
    select: fields,
    sort: { createdAt: "Descending" },
  });

  useEffect(() => {
    if (!fetching && !hasFetchedAllData) {
      setFields({
        ..._defaultPartialFields,
        contactsCount: true,
      });
      setHasFetchedAllData(true);
    }
    if (error && hasFetchedAllData) {
      setFields(_defaultPartialFields);
    }
  }, [data, error, fetching, hasFetchedAllData]);

  const tags = data || [];

  const changePage = useCallback(
    (_cursor, _pageNumber) => {
      setCursor(_cursor);
      setPageNumber(_pageNumber);

      // Only update URL parameter if we're not navigating from URL
      if (!isNavigatingFromUrl) {
        const searchParams = new URLSearchParams(location.search);
        if (_pageNumber === 1) {
          searchParams.delete("page");
        } else {
          searchParams.set("page", _pageNumber.toString());
        }

        const newSearch = searchParams.toString();
        const newUrl = newSearch
          ? `${location.pathname}?${newSearch}`
          : location.pathname;
        navigate(newUrl, { replace: true });
      }

      setCriteria({
        ..._cursor,
        filter: [{ customerId: { equals: customer?.id } }],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    },
    [
      location.search,
      location.pathname,
      navigate,
      customer?.id,
      isNavigatingFromUrl,
    ],
  );

  const getNextPage = () => {
    if (data?.endCursor) {
      changePage({ first: NUM_ON_PAGE, after: data.endCursor }, pageNumber + 1);
    }
  };

  // Handle navigation to correct page from URL parameter
  useEffect(() => {
    if (
      targetPageFromUrl &&
      targetPageFromUrl > 1 &&
      data &&
      data.length > 0 &&
      pageNumber < targetPageFromUrl &&
      !fetching
    ) {
      // We have data and need to navigate to the target page
      // Call getNextPage to move to the next page
      if (data?.endCursor) {
        getNextPage();
      }
    } else if (targetPageFromUrl && pageNumber === targetPageFromUrl) {
      // We've reached the target page, clear the target and navigation flag
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [data, targetPageFromUrl, pageNumber, fetching]);

  const getPreviousPage = () => {
    if (pageNumber > 1) {
      changePage(
        { last: NUM_ON_PAGE, before: data.startCursor },
        pageNumber - 1,
      );
    }
  };

  const handleRename = (tag) => {
    setRenamingTagId(tag.id);
    setRenameValue(tag.name);
    setTimeout(() => {
      renameInputRef.current?.focus();
    }, 0);
  };

  const handleRenameValidate = async (tag) => {
    if (!renameValue.trim() || renameValue === tag.name) {
      setRenamingTagId(null);
      return;
    }
    setIsRenameLoading(true);
    try {
      await api.tag.update(tag.id, { name: renameValue.trim() });
      // The data will be refreshed automatically by useFindMany
      setRenamingTagId(null);
      setIsRenameLoading(false);
    } catch (e) {
      console.log(e);
    }
  };

  const handleRenameInputKeyUp = (e, tag) => {
    if (e.key === "Enter") {
      handleRenameValidate(tag);
    }
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Tags"
          searchPlaceholder="Search tags..."
          search={search}
          setSearch={setSearch}
        />
        <OverflowFrame>
          {isLoadingToTargetPage && <LoadingSpinner />}
          <div className="mt-5 mx-auto">
            {fetching && !isLoadingToTargetPage ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              tags &&
              !isLoadingToTargetPage && (
                <>
                  {tags.length > 0 ? (
                    <Table className="mb-4">
                      <TableHead>
                        <TableRow>
                          <TableHeader>Tag</TableHeader>
                          <TableHeader>Created</TableHeader>
                          <TableHeader>Last contact added</TableHeader>
                          <TableHeader className="text-center">
                            Contacts
                          </TableHeader>
                          <TableHeader className="text-center">
                            Actions
                          </TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tags.map((tag) => (
                          <TableRow key={tag.id}>
                            <TableCell className="text-center">
                              {renamingTagId === tag.id ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Input
                                    ref={
                                      renamingTagId === tag.id
                                        ? renameInputRef
                                        : null
                                    }
                                    className=" leading-7 text-gray-900 dark:text-white"
                                    value={renameValue}
                                    onChange={(e) =>
                                      setRenameValue(e.target.value)
                                    }
                                    onKeyUp={(e) =>
                                      handleRenameInputKeyUp(e, tag)
                                    }
                                    style={{ maxWidth: "100%", margin: "0" }}
                                    autoComplete="off"
                                  />
                                  <Button
                                    outline
                                    className=" bg-white h-8 w-8"
                                    onClick={() => handleRenameValidate(tag)}
                                    loading={isRenameLoading}
                                  >
                                    <CheckIcon className="w-20 h-20" />
                                  </Button>
                                </div>
                              ) : (
                                <Badge color="zinc" className="w-full">
                                  <Text className="w-full flex items-center justify-center my-1">
                                    {tag.name}
                                  </Text>
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {tag.createdAt
                                ? formatDateTime(
                                    new Date(tag.createdAt),
                                    "mediumDate",
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {tag.lastContactTagCreatedAt
                                ? formatDateTime(
                                    new Date(tag.lastContactTagCreatedAt),
                                    "mediumDate",
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              {mergingTagIds.includes(tag.id) ? (
                                <span className="animated-dots" />
                              ) : tag.contactsCount ===
                                undefined ? undefined : (
                                (tag.contactsCount ?? 0)
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Dropdown>
                                <DropdownButton plain aria-label="More actions">
                                  <EllipsisHorizontalIcon />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                  <DropdownItem
                                    onClick={() => handleRename(tag)}
                                  >
                                    Rename
                                  </DropdownItem>
                                  {tag.contactsCount !== undefined &&
                                    tag.contactsCount > 0 && (
                                      <DropdownItem
                                        onClick={() => setTagToMerge(tag)}
                                      >
                                        Merge
                                      </DropdownItem>
                                    )}
                                  <DropdownItem
                                    onClick={() => setTagToDelete(tag)}
                                  >
                                    Delete
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8">
                      <Text className="text-gray-500">No tags found.</Text>
                    </div>
                  )}
                </>
              )
            )}
            {!search && !isLoadingToTargetPage && tags && tags.length > 0 && (
              <div className="flex justify-start my-4">
                <Button
                  color="secondaryButton"
                  onClick={getPreviousPage}
                  disabled={!data?.hasPreviousPage}
                >
                  <ArrowLeftIcon />
                  Previous
                </Button>
                <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
                  {pageNumber}
                </Badge>
                <Button
                  color="secondaryButton"
                  onClick={getNextPage}
                  disabled={!data?.hasNextPage}
                >
                  Next
                  <ArrowRightIcon />
                </Button>
              </div>
            )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
      <TagDeleteDialog
        open={!!tagToDelete}
        tagToDelete={tagToDelete}
        setTagToDelete={setTagToDelete}
        customerId={customer.id}
      />
      <TagMergeDialog
        open={!!tagToMerge}
        tagToMerge={tagToMerge}
        setTagToMerge={setTagToMerge}
        tags={tags}
        setMergingTagIds={setMergingTagIds}
        customerId={customer.id}
      />
    </>
  );
}
